import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import {
  CreateHomeworkAssignmentRequestBody,
  GetTopicsCourseType,
} from "@packages/api/madlenAPI.schemas";
import { CourseData, isTurkishCourse } from "@packages/constant/CourseData";
import CommonPropertiesStep from "./CommonPropertiesStep";
import { useToast } from "@packages/contexts/ToastContext";
import { Button, Text } from "../../../../../core";
import CourseSelector from "../course-selector";
import { UnitOutcomeSelector } from "../unit-outcome-selector";
import GradeSelector from "../../../../tools/components/grade-selector";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppSelector } from "@packages/store/hooks";
import OutputLanguageSelector from "@features/tools/components/create-modal/OutputLanguageSelector";

export type CurriculumType = "MEB" | "INTERNATIONAL";
export type InternationalCurriculum = "AP" | "IGCSE" | "IB" | "ALEVEL";

interface LearningOutcomeStepProps {
  assignmentData: CreateHomeworkAssignmentRequestBody;
  onChange: (data: Partial<CreateHomeworkAssignmentRequestBody>) => void;
}

const LearningOutcomeStep = ({
  assignmentData,
  onChange,
}: LearningOutcomeStepProps) => {
  const { t, i18n } = useTranslation();
  const { addToast } = useToast();
  const location = useLocation();
  const navigate = useNavigate();
  const [isMaarif, setIsMaarif] = useState(() => {
    const savedValue = localStorage.getItem("isMaarifEnabled");
    return savedValue
      ? JSON.parse(savedValue)
      : location.state?.isMaarif ||
          assignmentData.generator === "MAARIF_LEARNING_OUTCOME" ||
          false;
  });

  // Verileri mevcut assignmentData'dan çek ya da varsayılan değer ata
  const [selectedGrade, setSelectedGrade] = useState<number>(
    assignmentData.grade ?? 8,
  );
  const [selectedCourse, setSelectedCourse] = useState<GetTopicsCourseType>(
    assignmentData.courseType ??
      (i18n.language === "tr" ? "HISTORY" : "AP_2025_WORLD_HISTORY_MODERN"),
  );
  const [curriculumType, setCurriculumType] = useState<CurriculumType>("MEB");
  const [internationalCurriculum, setInternationalCurriculum] =
    useState<InternationalCurriculum>("AP");
  const [selectedOutcomes, setSelectedOutcomes] = useState<number[]>(
    assignmentData.learningOutcomeIds ??
      assignmentData.maarifLearningOutcomeIds ??
      [],
  );
  const [selectedLanguage, setSelectedLanguage] = useState(
    assignmentData.contentLanguage || i18n.language,
  );
  const userEmail = useAppSelector((state) => state.user.teacher?.email);
  const shouldShowMathematics =
    userEmail === "batuhan@madlen.io" || userEmail === "kadriyezobu@gmail.com";
  // Dil değiştiğinde yönlendirme
  useEffect(() => {
    if (
      i18n.language === "en" &&
      location.pathname.includes("/learning_outcome")
    ) {
      navigate("/assignments/generate");
    }
  }, [i18n.language, location.pathname]);

  // Maarif durumu localStorage'a kaydet
  useEffect(() => {
    localStorage.setItem("isMaarifEnabled", JSON.stringify(isMaarif));
  }, [isMaarif]);

  // İlk yükleme veya dil değiştiğinde
  useEffect(() => {
    // Eğer assignmentData.courseType zaten ayarlanmışsa, mevcut değeri koru
    if (!assignmentData.courseType) {
      const newCourse =
        i18n.language === "tr" ? "HISTORY" : "AP_2025_WORLD_HISTORY_MODERN";
      setSelectedCourse(newCourse);

      // Sadece course type değiştiğinde outcomes'i sıfırla
      if (selectedCourse !== newCourse) {
        setSelectedOutcomes([]);
        onChange({
          courseType: newCourse,
          learningOutcomeIds: [],
          maarifLearningOutcomeIds: [],
        });
      } else {
        onChange({
          courseType: newCourse,
        });
      }
    }
  }, [i18n.language, assignmentData.courseType]);

  // Component mount olduğunda generator ayarla
  useEffect(() => {
    if (!assignmentData.generator) {
      onChange({
        generator: isMaarif ? "MAARIF_LEARNING_OUTCOME" : "LEARNING_OUTCOME",
      });
    }
  }, []);

  // Handle curriculum type changes
  useEffect(() => {
    const availableCourses = getFilteredCourses();
    const firstAvailableCourse = availableCourses[0];
    const doesPreviousCourseExist = availableCourses.includes(selectedCourse);

    if (!doesPreviousCourseExist && firstAvailableCourse) {
      setSelectedCourse(firstAvailableCourse);
      setSelectedOutcomes([]);
      onChange({
        courseType: firstAvailableCourse,
        learningOutcomeIds: [],
        maarifLearningOutcomeIds: [],
      });
    }
  }, [curriculumType, internationalCurriculum, selectedGrade]);

  // Component mount olduğunda - assignmentData değişikliklerinde mevcut değerleri güncelle
  useEffect(() => {
    if (assignmentData.grade && assignmentData.grade !== selectedGrade) {
      setSelectedGrade(assignmentData.grade);
    }

    if (
      assignmentData.courseType &&
      assignmentData.courseType !== selectedCourse
    ) {
      setSelectedCourse(assignmentData.courseType);
    }

    if (
      !isMaarif &&
      assignmentData.learningOutcomeIds &&
      JSON.stringify(assignmentData.learningOutcomeIds) !==
        JSON.stringify(selectedOutcomes)
    ) {
      setSelectedOutcomes(assignmentData.learningOutcomeIds);
    }

    if (
      isMaarif &&
      assignmentData.maarifLearningOutcomeIds &&
      JSON.stringify(assignmentData.maarifLearningOutcomeIds) !==
        JSON.stringify(selectedOutcomes)
    ) {
      setSelectedOutcomes(assignmentData.maarifLearningOutcomeIds);
    }

    if (assignmentData.generator === "MAARIF_LEARNING_OUTCOME" && !isMaarif) {
      setIsMaarif(true);
    } else if (assignmentData.generator === "LEARNING_OUTCOME" && isMaarif) {
      setIsMaarif(false);
    }
  }, [assignmentData, isMaarif]);

  const getFilteredCourses = (): GetTopicsCourseType[] => {
    if (curriculumType === "MEB") {
      return Object.keys(CourseData).filter(
        (key) =>
          isTurkishCourse(key) &&
          CourseData[key as GetTopicsCourseType].visible(selectedGrade),
      ) as GetTopicsCourseType[];
    } else {
      return Object.keys(CourseData).filter(
        (key) =>
          key.startsWith(`${internationalCurriculum}_`) &&
          CourseData[key as GetTopicsCourseType].visible(selectedGrade),
      ) as GetTopicsCourseType[];
    }
  };

  return (
    <div className="space-y-6">
      {i18n.language === "tr" && (
        <div className="bg-blue-50/90 dark:bg-blue-900/50 p-4 rounded-lg mb-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <Text variant="p" status="info" size="sm">
                {isMaarif
                  ? "Türkiye Yüzyılı Maarif Modeli Kazanımları"
                  : t("assignments.generator.topic.title")}
              </Text>
              <Text variant="p" status="info" size="sm">
                {isMaarif
                  ? "Maarif müfredatına uygun kazanımları seçin"
                  : t("assignments.generator.topic.description")}
              </Text>
            </div>
            {selectedGrade > 4 && (
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={isMaarif}
                  onChange={(e) => {
                    setIsMaarif(e.target.checked);
                    // Maarif değiştiğinde outcome'ları temizle
                    setSelectedOutcomes([]);
                    onChange({
                      generator: e.target.checked
                        ? "MAARIF_LEARNING_OUTCOME"
                        : "LEARNING_OUTCOME",
                      learningOutcomeIds: [],
                      maarifLearningOutcomeIds: [],
                    });
                  }}
                  className="sr-only peer"
                />
                <div
                  className={`w-11 h-6 rounded-full peer relative transition-colors duration-300
                      ${isMaarif ? "bg-blue-600 dark:bg-blue-600" : "bg-gray-200 dark:bg-gray-700"}
                      peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800`}
                ></div>
                <div
                  className={`absolute h-5 w-5 rounded-full bg-white border border-gray-300 
                      transition-transform duration-300 top-[2px] start-[2px]
                      ${isMaarif ? "translate-x-5 rtl:-translate-x-5" : ""}`}
                ></div>
                <span className="ms-3 text-sm font-medium text-gray-900 dark:text-gray-300">
                  Türkiye Yüzyılı Maarif Modeli
                </span>
              </label>
            )}
          </div>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <CommonPropertiesStep
          assignmentData={assignmentData}
          onChange={onChange}
        />
        <div className="space-y-2.5">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-1.5">
            <GradeSelector
              selectedGrade={selectedGrade}
              minGrade={1}
              setSelectedGrade={(grade) => {
                const availableCourses = Object.entries(CourseData).filter(
                  ([, value]) => value.visible(grade),
                );
                // if previous course is in availableCourses, do not change selectedCourse else use first available course
                const firstAvailableCourse =
                  availableCourses[0]?.[0] as GetTopicsCourseType;
                const doesPreviousCourseExist = availableCourses.some(
                  ([key]) => key === selectedCourse,
                );

                setSelectedGrade(grade);
                if (!doesPreviousCourseExist) {
                  setSelectedCourse(firstAvailableCourse);
                }
                setSelectedOutcomes([]);
                if (grade < 5) {
                  setIsMaarif(true);
                  onChange({
                    generator: "MAARIF_LEARNING_OUTCOME",
                  });
                }
                onChange({
                  grade,
                  // if previous course is in availableCourses, do not change selectedCourse else use first available course
                  ...(!doesPreviousCourseExist && {
                    courseType: firstAvailableCourse,
                  }),
                  learningOutcomeIds: [],
                  maarifLearningOutcomeIds: [],
                });
              }}
            />
          </div>
          <h4
            className={`text-sm font-medium text-gray-700 dark:text-gray-200`}
          >
            {t("hooks.form.outputLanguage")}
          </h4>
          <OutputLanguageSelector
            selectedLanguage={selectedLanguage}
            onLanguageChange={(language) => {
              setSelectedLanguage(language);
              onChange({ contentLanguage: language });
            }}
          />
        </div>
      </div>

      <div className="space-y-4">
        {/* Curriculum Type Selection */}
        <div className="space-y-2">
          <h4
            className={`text-sm font-medium text-gray-700 dark:text-gray-200`}
          >
            {t("common.curriculumType")}
          </h4>
          <div className="grid grid-cols-1 gap-3">
            <Button
              type="button"
              onClick={() => {
                setCurriculumType("MEB");
                setSelectedOutcomes([]);
              }}
              variant="solid"
              size="md"
              rounded="lg"
              textColor={
                curriculumType === "MEB"
                  ? "text-white"
                  : "text-gray-600 dark:text-gray-400"
              }
              bgColor={
                curriculumType === "MEB"
                  ? `bg-indigo-600 hover:bg-indigo-700`
                  : "bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600"
              }
              fullWidth
            >
              <span className="text-sm font-medium">
                {t("common.nationalCurriculum")}
              </span>
            </Button>
          </div>
        </div>

        {/* International Curriculum Sub-selection */}
        {curriculumType === "INTERNATIONAL" && (
          <div className="space-y-2">
            <h4
              className={`text-sm font-medium text-gray-700 dark:text-gray-200`}
            >
              {t("common.selectInternationalProgram")}
            </h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {(
                ["AP", "IGCSE", "IB", "ALEVEL"] as InternationalCurriculum[]
              ).map((curriculum) => (
                <Button
                  key={curriculum}
                  type="button"
                  onClick={() => {
                    setInternationalCurriculum(curriculum);
                    setSelectedOutcomes([]);
                  }}
                  variant="solid"
                  size="md"
                  rounded="lg"
                  textColor={
                    internationalCurriculum === curriculum
                      ? "text-white"
                      : "text-gray-600 dark:text-gray-400"
                  }
                  bgColor={
                    internationalCurriculum === curriculum
                      ? `bg-indigo-600 hover:bg-indigo-700`
                      : "bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600"
                  }
                  fullWidth
                >
                  <span className="text-sm font-medium">{curriculum}</span>
                </Button>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1">
        <div className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <CourseSelector
              selectedGrade={selectedGrade}
              selectedCourse={selectedCourse}
              filteredCourses={Object.values(GetTopicsCourseType)
                .filter((course) => !getFilteredCourses().includes(course))
                .concat(
                  shouldShowMathematics
                    ? []
                    : (["MATHEMATICS"] as GetTopicsCourseType[]),
                )}
              setSelectedCourse={(course) => {
                setSelectedCourse(course);
                setSelectedOutcomes([]);
                onChange({
                  courseType: course,
                  learningOutcomeIds: [],
                  maarifLearningOutcomeIds: [],
                });
              }}
            />
          </div>
        </div>
      </div>

      {selectedGrade && selectedCourse && (
        <UnitOutcomeSelector
          grade={selectedGrade}
          courseType={selectedCourse}
          selectedOutcomes={selectedOutcomes}
          isMaarif={isMaarif}
          setSelectedOutcomes={(outcomeIds) => {
            // if (outcomeIds.length > 3) {
            //   addToast({
            //     title: t("notifications.assignment.topicLimit"),
            //     variant: "warning",
            //     duration: 3000,
            //   });
            //   return;
            // }
            setSelectedOutcomes(outcomeIds);

            if (isMaarif) {
              onChange({
                maarifLearningOutcomeIds: outcomeIds,
              });
            } else {
              onChange({
                learningOutcomeIds: outcomeIds,
              });
            }
          }}
        />
      )}
    </div>
  );
};

export default LearningOutcomeStep;
